/**
 * Weekly Drill service (W9 rhythm)
 *
 * The dept admin sets a cadence; a stamped drill doc exists per week at
 * `weekly_drills/{deptId}_{weekKey}` with per-officer completions under it.
 * Reads are direct Firestore gets (deterministic ids); writes go through
 * callables.
 */

import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { initFirebaseClient } from '@copapp/firebase/client';
import { COLLECTIONS } from '@copapp/core/constants';
import { isoWeekKey } from '@copapp/core';

export const DRILL_CATEGORY_LABELS: Record<string, string> = {
  general: 'General Call',
  trafficStops: 'Traffic Stop',
  domestics: 'Domestic',
  publicOrder: 'Public Order',
  crisisIntervention: 'Crisis / Mental Health',
};

export interface WeeklyDrillConfig {
  enabled: boolean;
  kind: 'category' | 'dojo';
  category: string | null;
  dojoScenarioId: string | null;
  dojoScenarioTitle: string | null;
  difficulty: string;
  passTarget: number;
  updatedBy?: string;
  updatedAt?: string;
}

export interface WeeklyDrillCompletion {
  officerUid: string;
  bestScore: number;
  bestSessionId: string;
  passTarget: number;
  passed: boolean;
  attempts: number;
  firstCompletedAt: string;
  updatedAt: string;
}

export interface WeeklyDrillStatus {
  config: WeeklyDrillConfig;
  weekKey: string;
  drillId: string;
  completion: WeeklyDrillCompletion | null;
}

/** Human title for the drill: dojo scenario name or category label. */
export function drillTitle(config: WeeklyDrillConfig): string {
  if (config.kind === 'dojo') return config.dojoScenarioTitle || 'Dojo scenario';
  return DRILL_CATEGORY_LABELS[config.category || ''] || config.category || 'General Call';
}

/**
 * The officer's view of this week's drill: the dept cadence + their own
 * completion record. Null when no drill is configured (card renders nothing).
 */
export async function getWeeklyDrillStatus(
  departmentId: string,
  officerUid: string,
): Promise<WeeklyDrillStatus | null> {
  const { db } = initFirebaseClient();

  const deptSnap = await getDoc(doc(db, COLLECTIONS.DEPARTMENTS, departmentId));
  const config = deptSnap.data()?.weeklyDrill as WeeklyDrillConfig | undefined;
  if (!config?.enabled) return null;

  const weekKey = isoWeekKey(new Date());
  const drillId = `${departmentId}_${weekKey}`;
  const completionSnap = await getDoc(doc(db, COLLECTIONS.WEEKLY_DRILLS, drillId, 'completions', officerUid));

  return {
    config,
    weekKey,
    drillId,
    completion: completionSnap.exists() ? (completionSnap.data() as WeeklyDrillCompletion) : null,
  };
}

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export interface SetCadenceParams {
  departmentId: string;
  enabled: boolean;
  kind?: 'category' | 'dojo';
  category?: string;
  dojoScenarioId?: string;
  difficulty?: string;
  passTarget?: number;
}

export async function setWeeklyDrillCadence(
  params: SetCadenceParams,
): Promise<{ enabled: boolean; weekKey?: string; stamped?: boolean }> {
  const fn = httpsCallable<SetCadenceParams, { success: boolean; data: { enabled: boolean; weekKey?: string; stamped?: boolean } }>(
    initFirebaseClient().functions,
    'setWeeklyDrillCadence',
  );
  return (await fn(params)).data.data;
}

export interface StartDrillResult {
  sessionId: string;
  scenario: Record<string, unknown>;
  worldState: Record<string, unknown>;
  passTarget: number;
}

export async function startWeeklyDrill(departmentId: string): Promise<StartDrillResult> {
  const fn = httpsCallable<{ departmentId: string }, { success: boolean; data: StartDrillResult }>(
    initFirebaseClient().functions,
    'startWeeklyDrill',
    { timeout: 120_000 },
  );
  return (await fn({ departmentId })).data.data;
}

export interface RhythmStreaks {
  weekKey: string;
  officerStreak: number;
  officerTrainedThisWeek: boolean;
  deptStreak: number;
}

/** Consecutive weeks trained — the officer's own and the department's. */
export async function getRhythmStreaks(departmentId: string): Promise<RhythmStreaks> {
  const fn = httpsCallable<{ departmentId: string }, { success: boolean; data: RhythmStreaks }>(
    initFirebaseClient().functions,
    'getRhythmStreaks',
  );
  return (await fn({ departmentId })).data.data;
}
